import { CHAT_REFUSAL } from '@continuum/show-core';
import type { Lang } from '../ui/text.js';

/**
 * The words of the messages, in English and bokmål. Plain and short, like everything else a guest
 * reads with a glass in one hand. "Saboteurs" is the one word the crew never sees here: that part
 * only ever shows on a saboteur's own phone.
 */
export interface ChatCopy {
  /** The header button, read aloud; with how many are new. */
  button: (unread: number) => string;
  title: string;
  lede: string;
  close: string;
  back: string;
  seat: (seat: number) => string;
  nobodyYet: string;
  saboteurs: string;
  /** Under the saboteurs' row: who else is in it. */
  saboteursWho: (names: string) => string;
  saboteursEmpty: string;
  /** The whole table's conversation. */
  everyone: string;
  everyoneWho: string;
  everyoneEmpty: string;
  threadEmpty: (name: string) => string;
  you: string;
  placeholder: string;
  send: string;
  charsLeft: (n: number) => string;
  sending: string;
  tapToEdit: string;
  /** Why a message did not go. */
  notSent: (why: string) => string;
  /** Under the guest's own latest message. */
  sent: string;
  readAt: (time: string) => string;
  readBy: (names: string) => string;
  readByAll: string;
  /** The notice at the top when a message comes in: who wrote, never what (Marek, 2026-10-24). */
  notice: (names: string, many: boolean) => string;
  noticeTap: string;
  problem: {
    offline: string;
    noAnswer: string;
    slow: string;
    long: string;
    gone: string;
    full: string;
    channel: string;
    other: string;
  };
  and: string;
}

const en: ChatCopy = {
  button: (unread) => (unread > 0 ? `Messages, ${unread} new` : 'Messages'),
  title: 'MESSAGES',
  lede: 'Write to everyone at the table, or to one guest: then only the two of you see it.',
  close: 'Close',
  back: 'Back',
  seat: (seat) => `SEAT ${String(seat).padStart(2, '0')}`,
  nobodyYet: 'Nobody else has checked in yet.',
  saboteurs: 'SABOTEURS',
  saboteursWho: (names) => `Only you and ${names} see this.`,
  saboteursEmpty: 'Only the saboteurs see this. Plan your next move.',
  everyone: 'EVERYONE',
  everyoneWho: 'Everyone at the table sees this.',
  everyoneEmpty: 'Say something to the whole table. Everyone checked in sees it.',
  threadEmpty: (name) => `Say hello to ${name}. Only the two of you see this.`,
  you: 'You',
  placeholder: 'Write a message',
  send: 'SEND',
  charsLeft: (n) => `${n} left`,
  sending: 'Sending…',
  tapToEdit: 'Tap to try again.',
  notSent: (why) => `Not sent: ${why}`,
  sent: '✓ SENT',
  readAt: (time) => `✓✓ READ ${time}`,
  readBy: (names) => `✓✓ READ BY ${names}`,
  readByAll: '✓✓ READ BY EVERYONE',
  notice: (names, many) => `${names} · ${many ? 'NEW MESSAGES' : 'NEW MESSAGE'}`,
  noticeTap: 'Tap to read.',
  problem: {
    offline: 'no connection.',
    noAnswer: 'no answer from the ship.',
    slow: 'slow down a little.',
    long: 'it is too long.',
    gone: 'they are no longer aboard.',
    full: 'no more messages tonight.',
    channel: 'the saboteurs have changed.',
    other: 'something went wrong.',
  },
  and: 'and',
};

const no: ChatCopy = {
  button: (unread) => (unread > 0 ? `Meldinger, ${unread} nye` : 'Meldinger'),
  title: 'MELDINGER',
  lede: 'Skriv til alle ved bordet, eller til én gjest: da ser bare dere to det.',
  close: 'Lukk',
  back: 'Tilbake',
  seat: (seat) => `SETE ${String(seat).padStart(2, '0')}`,
  nobodyYet: 'Ingen andre har sjekket inn ennå.',
  saboteurs: 'SABOTØRER',
  saboteursWho: (names) => `Bare du og ${names} ser dette.`,
  saboteursEmpty: 'Bare sabotørene ser dette. Planlegg neste trekk.',
  everyone: 'ALLE',
  everyoneWho: 'Alle ved bordet ser dette.',
  everyoneEmpty: 'Si noe til hele bordet. Alle som har sjekket inn, ser det.',
  threadEmpty: (name) => `Si hei til ${name}. Bare dere to ser dette.`,
  you: 'Du',
  placeholder: 'Skriv en melding',
  send: 'SEND',
  charsLeft: (n) => `${n} igjen`,
  sending: 'Sender …',
  tapToEdit: 'Trykk for å prøve igjen.',
  notSent: (why) => `Ikke sendt: ${why}`,
  sent: '✓ SENDT',
  readAt: (time) => `✓✓ LEST ${time}`,
  readBy: (names) => `✓✓ LEST AV ${names}`,
  readByAll: '✓✓ LEST AV ALLE',
  notice: (names, many) => `${names} · ${many ? 'NYE MELDINGER' : 'NY MELDING'}`,
  noticeTap: 'Trykk for å lese.',
  problem: {
    offline: 'ingen forbindelse.',
    noAnswer: 'skipet svarte ikke.',
    slow: 'ta det litt roligere.',
    long: 'den er for lang.',
    gone: 'personen er ikke lenger om bord.',
    full: 'ikke flere meldinger i kveld.',
    channel: 'sabotørene er byttet ut.',
    other: 'noe gikk galt.',
  },
  and: 'og',
};

export const CHAT_COPY: Record<Lang, ChatCopy> = { en, no };

/** Problems the phone finds out for itself, beside the room's own refusals. */
export const OFFLINE = 'offline';
export const NO_ANSWER = 'no answer';

/** Why a message did not go, in this language. */
export function problemText(error: string, lang: Lang): string {
  const p = CHAT_COPY[lang].problem;
  switch (error) {
    case OFFLINE:
      return p.offline;
    case NO_ANSWER:
    case 'seat not claimed':
      return p.noAnswer;
    case CHAT_REFUSAL.slow:
      return p.slow;
    case CHAT_REFUSAL.long:
      return p.long;
    case CHAT_REFUSAL.nobody:
      return p.gone;
    case CHAT_REFUSAL.full:
      return p.full;
    case CHAT_REFUSAL.channel:
      return p.channel;
    default:
      return p.other;
  }
}

/** "A", "A and B", "A, B and C". */
export function names(list: readonly string[], lang: Lang): string {
  if (list.length <= 1) return list[0] ?? '';
  return `${list.slice(0, -1).join(', ')} ${CHAT_COPY[lang].and} ${list[list.length - 1]}`;
}
